import { z } from 'zod';
import { InvoiceDocumentTypeEnum, InvoiceStatusEnum } from './invoice-blob.js';

/**
 * Multipart upload request metadata. The actual file is read from the
 * multipart body; this schema validates the JSON-encoded form fields.
 * `tenantId` may be omitted when the request scope has exactly one active
 * visible tenant and the backend can infer it unambiguously.
 */
export const UploadRequestSchema = z
  .object({
    tenantId: z.string().min(1).optional(),
    documentType: InvoiceDocumentTypeEnum.optional(),
    /** Legacy alias for older clients. */
    customerId: z.string().min(1).optional(),
    /**
     * Legacy compatibility flag. Duplicate uploads are processed as fresh
     * invoice records by default, so callers do not need to set this in v1.
     */
    force: z.boolean().optional(),
  })
  .transform(({ tenantId, customerId, documentType, force }) => ({
    ...((tenantId ?? customerId) ? { tenantId: tenantId ?? customerId } : {}),
    ...(documentType !== undefined ? { documentType } : {}),
    ...(force !== undefined ? { force } : {}),
  }));
export type UploadRequest = z.infer<typeof UploadRequestSchema>;

export const UploadResponseSchema = z.object({
  invoiceId: z.string(),
  status: InvoiceStatusEnum,
  /** Hex-encoded SHA-256 of the uploaded bytes for traceability. */
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  /** Reserved compatibility field; duplicate uploads currently return `false`. */
  idempotent: z.boolean(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;
